import { BRAND } from "constants/brand";
import { useEffect, useState } from "react";
import { Ring } from "@uiball/loaders";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import useOrder from "hooks/useOrder";
import UserNote from "components/Ui/UserNote/UserNote";
import {
    Field,
    StatusPill,
    OrderTimeline,
    CustomerTimeline,
    DriverCard,
    OrderMoney,
} from "components/Ui/OrderDetail/OrderDetailParts";

/**
 * /deliveryOrders/:id — the PRODUCTLESS order detail page.
 *
 * It exists because three of the four order entry points create orders with no
 * cart lines (vendor driver requests, transcribed WhatsApp orders, quick
 * dispatch). Order/OrderDetails/OrderDetails.js renders a product table and a
 * receipt; there is nothing to render for those, so this variant shows the
 * money as three plain figures (قيمه الطلب / الشحن / المجموع) plus the parties
 * involved. That is why the /orders row sends every non-normal order type here
 * rather than to its own detail page.
 *
 * Read-only by design: this is the page an operator opens while on the phone
 * with a customer, a vendor or a driver, so it surfaces all three sets of
 * contact details — including the DRIVER's name and phone, which the /orders
 * table never shows.
 *
 * ACCESS: the route carries a bare `<ProtectedRoute>` with no permission
 * condition (App.js), unlike /orders/:id which requires `manage_orders`. Any
 * authenticated dashboard user, vendor-role included, can open any order id
 * here. Only the internal customer note is role-gated.
 *
 * KNOWN CONSTRAINTS (documented, not fixed):
 * - Uses `useOrder` (the /orders hook) rather than `useDeliveryOrder`. Only
 *   `fetchOrderBYId` is used and the two hooks implement it identically, so the
 *   choice is arbitrary; mounting `useOrder` additionally registers its
 *   URL-params effect, which is inert on this route.
 * - The body renders before the fetch resolves with `order === null`, so every
 *   field paints empty for one frame beneath the spinner.
 * - `order.lat` is a SECOND PHONE NUMBER, not a latitude, hence the
 *   "هاتف الزبون 2" label.
 * - The status lookup table comes from constants/orderStatus.js.
 */
const DeliveryOrderDetails = (props) => {
    const adminInfo = useSelector((state) => state.auth.userData);
    const canEditNotes = !!(adminInfo?.super_admin || adminInfo?.roles?.manage_users);
    const { loading, fetchOrderBYId } = useOrder();
    const [order, setOrder] = useState(null);
    const [date, setDate] = useState(new Date());
    const { id } = useParams();

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                const response = await fetchOrderBYId(id);
                setOrder(response);
                const newDate = new Date(response.createdAt);
                // newDate.setHours(newDate.getHours() + 1); // Add 1 hour
                setDate(newDate);
            } catch (error) {
                console.log(error);
            }
        };
        fetchOrder();
    }, [id]);

    return (
        <div className="add-product order-page">
            {loading ? (
                <div className="loading-spinner center">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                </div>
            ) : null}

            <div className="order-page__head">
                <div className="order-page__title">
                    <h3>الطلب رقم {order?.id}</h3>
                    <StatusPill status={order?.status} />
                </div>
            </div>

            <OrderMoney order={order} createdAt={date} />

            {/* THREE PARTIES, THREE CARDS — the same shape as /orders/:id, which
             * shows the same order to the same operator. This page was a wall
             * of `label: value` rows in three unequal bootstrap columns, so the
             * short ones ended early and left a hole the width of the page. */}
            <div className="order-cards">
                <section className="surface-card order-card">
                    <h4 className="order-card__title">الزبون</h4>
                    <Field label="الاسم">{order?.name}</Field>
                    <Field label="الهاتف">{order?.phone}</Field>
                    {/* `order.lat` is a SECOND PHONE NUMBER, not a latitude. */}
                    {order?.lat && order.lat.trim() !== order?.phone?.trim() ? (
                        <Field label="هاتف آخر">{order.lat}</Field>
                    ) : null}
                    <Field label="المنطقة">{order?.area?.name}</Field>
                    <Field label="العنوان" wide>
                        {order?.address}
                    </Field>
                    <Field label="ملاحظات" wide>
                        {order?.notes}
                    </Field>
                    {order?.user && adminInfo?.role !== "vendor" ? (
                        <Field label="ملاحظة عن الزبون" wide>
                            <UserNote
                                userId={order.user.id}
                                note={order.user.note}
                                userName={order.user.name || order?.name}
                                editable={canEditNotes}
                                expand
                                showMessage={props.showMessage}
                            />
                        </Field>
                    ) : null}
                </section>

                <section className="surface-card order-card">
                    <h4 className="order-card__title">المطعم</h4>
                    <Field label="الاسم">{order?.vendor?.user?.name}</Field>
                    <Field label="الهاتف">{order?.vendor?.user?.phone}</Field>
                    <Field label="نوع الطلب">
                        {order?.fulfillment_method === "pickup" ? "استلام من المطعم" : "توصيل"}
                    </Field>
                </section>

                <DriverCard order={order} />
            </div>

            <OrderTimeline order={order} />
            <CustomerTimeline order={order} />
        </div>
    );
};
export default DeliveryOrderDetails;
